// Layers for reviewing a pull request the way it was built rather than in
// alphabetical order: what is stored first, then the rules, the plumbing, the
// interface, the tests, the build, the docs. Pure, so the view and the tests
// agree on where a file belongs.

export interface Layer {
  id: string
  title: string
  blurb: string
}

export const LAYERS: Layer[] = [
  {
    id: 'data',
    title: 'Data and schema',
    blurb: 'What is stored and its shape: migrations, queries, schemas, CRDs.',
  },
  {
    id: 'domain',
    title: 'Rules',
    blurb: 'How things behave: kinds’ actions, reconcilers, the pure libraries.',
  },
  { id: 'server', title: 'Plumbing', blurb: 'Handlers, connectors, the API client and sync.' },
  { id: 'ui', title: 'Interface', blurb: 'Views, components and styles.' },
  { id: 'tests', title: 'Tests', blurb: 'Unit tests, shared fixtures and end-to-end specs.' },
  { id: 'infra', title: 'Build and deploy', blurb: 'Charts, Dockerfiles, CI and dependencies.' },
  { id: 'docs', title: 'Docs', blurb: 'Docs and briefings.' },
  { id: 'other', title: 'Other', blurb: '' },
]

/** The layer a changed file belongs to. */
export const layerOf = (path: string): string => {
  const p = path.toLowerCase()
  if (/(^|\/)(e2e|testdata|__tests__)\//.test(p) || /(_test\.go|\.test\.tsx?|\.spec\.tsx?)$/.test(p))
    return 'tests'
  if (/\.(md|mdx)$/.test(p) || p.startsWith('docs/')) return 'docs'
  if (
    /(^|\/)(migrations?|queries|sqlcgen|crds?)\//.test(p) ||
    /\.sql$/.test(p) ||
    /(^|\/)schema\.ts$/.test(p) ||
    /(_types\.go|zz_generated[^/]*\.go)$/.test(p)
  )
    return 'data'
  if (
    /(^|\/)(charts|deploy|\.forgejo|\.github)\//.test(p) ||
    /(^|\/)(dockerfile|justfile|makefile)$/.test(p) ||
    /(^|\/)(go\.mod|go\.sum|package\.json|pnpm-lock\.yaml|vite\.config\.ts)$/.test(p) ||
    /(^|\/)tsconfig[^/]*\.json$/.test(p) ||
    /\.ya?ml$/.test(p)
  )
    return 'infra'
  if (/\.(tsx|css|html|svg)$/.test(p)) return 'ui'
  if (/(^|\/)(kinds|reconcile|lib|state|run)\//.test(p) || /(^|\/)apply\.ts$/.test(p)) return 'domain'
  if (/\.(go|ts|js|py|php|sh)$/.test(p)) return 'server'
  return 'other'
}

/** Files grouped by layer, in layer order, empty layers left out. */
export const byLayer = <F extends { path: string }>(files: F[]): { layer: Layer; files: F[] }[] =>
  LAYERS.map((layer) => ({ layer, files: files.filter((f) => layerOf(f.path) === layer.id) })).filter(
    (g) => g.files.length > 0,
  )
